<template>
	<SuiListGroup v-close-popper :list-data="quickActionsList" />
</template>

<script setup lang="ts">
import { SuiListGroup } from '@developers/scoro-ui';
import { type SuiListItemData, type SuiListGroupData } from '@/types';
import { useMfeUtils, usePermissions, useTranslation } from '@/composables';
import {
	type TaskListProjectFragment,
	type ProjectPhase,
} from '@/generated/graphql';
import { useTaskListStore } from './store';
import { UNGROUPED_TASKS_ID } from './constants';
import { Constants } from '@/constants';
import { computed } from 'vue';
import { PermissionAction } from '@/constants/permissions';

export type TaskListActionsProps = {
	project?: TaskListProjectFragment;
	projectPhaseId?: ProjectPhase['id'];
	addTaskSetLabel?: string;
};

const props = defineProps<TaskListActionsProps>();

const emit = defineEmits<{
	(e: 'open-phases-modal'): void;
}>();

const { t } = useTranslation();
const { rootEmit } = useMfeUtils();
const taskListStore = useTaskListStore();
const { can, userHas } = usePermissions();

const canModifyProject = computed(
	() => props.project?.permissions?.canModify || false
);

const canCreateTasks = computed(() =>
	can(PermissionAction.CREATE, Constants.Type.Task)
);

const addTaskSetLabelTranslation = computed(
	() => props.addTaskSetLabel || t('add_task_set')
);

const quickActionsList = computed<SuiListGroupData[]>(() => {
	const items: SuiListItemData[] = [];

	if (
		props.projectPhaseId &&
		(canCreateTasks.value || canModifyProject.value)
	) {
		items.push({
			id: 'task-list-add-task',
			title: t('task_list_add_task'),
			icon: {
				name: 'icon-task-16',
			},
			click: () => {
				rootEmit('show-event-popup', {
					eventType: 'task',
					project_id: props.project?.id,
					company_id: props.project?.company?.id,
					project_phase_id: props.projectPhaseId,
				});
			},
		});
	}

	if (userHas(Constants.Feature.PROJECT_PHASES) && canModifyProject.value) {
		items.push({
			id: 'task-list-add-phase',
			title: t('modify_phases'),
			icon: {
				name: 'icon-phase-16',
			},
			click: () => {
				emit('open-phases-modal');
			},
		});
	}

	if (canModifyProject.value || canCreateTasks.value) {
		items.push({
			id: 'task-list-add-task-bundle',
			title: addTaskSetLabelTranslation.value,
			icon: {
				name: 'icon-taskbundle-16',
			},
			click: () => {
				taskListStore.taskBundleOpen =
					props.projectPhaseId || UNGROUPED_TASKS_ID;
			},
		});
	}

	if (
		!props.projectPhaseId &&
		props.project?.createTasksFromQuoteId &&
		(canCreateTasks.value || canModifyProject.value)
	) {
		items.push({
			id: 'task-list-add-tasks-quote',
			title: t('project_empty_task_list_quote_to_task'),
			icon: {
				name: 'icon-task-new-16',
			},
			click: () => {
				window.location.href = `/${Constants.Module.QUOTES.name}/${Constants.Feature.QUOTE_TO_TASKS}/${props.project?.createTasksFromQuoteId}`;
			},
		});
	}

	return [
		{
			id: 'task-list-quick-actions',
			items,
		},
	];
});
</script>
